import apiClient from '@/shared/api/client';
import type { ApiResponse } from '@/shared/api/types';

export interface LocationCredentials {
  legalName: string;
  tinLast4: string;
  groupNpi: string | null;
  fax: string | null;
  updatedAt: string;
}

export interface LocationCredentialsInput {
  locationId: string;
  legalName: string;
  // Optional — omit to keep the existing TIN unchanged; required on first save.
  tin?: string;
  // Optional — solo practitioners often have no Type 2 NPI distinct from
  // their own individual NPI.
  groupNpi?: string;
  fax?: string;
}

export interface ProviderCredentialRow {
  staffMemberId: string;
  name: string;
  individualNpi: string | null;
  licenseNumber: string | null;
  licenseState: string | null;
}

export interface ProviderCredentialInput {
  locationId: string;
  individualNpi: string;
  licenseNumber?: string;
  licenseState?: string;
}

export interface AddProviderInput {
  locationId: string;
  name: string;
  individualNpi: string;
  licenseNumber?: string;
  licenseState?: string;
}

export interface SetupReadiness {
  ready: boolean;
  hardGates: {
    legalNamePresent: boolean;
    tinValid: boolean;
    groupNpiValid: boolean;
    hasProviderWithValidNpi: boolean;
    licensesCompleteWhereRequired: boolean;
    hasAtLeastOnePayer: boolean;
  };
  softStates: {
    payersWithoutActiveMap: string[];
  };
}

export const setupApi = {
  getLocationCredentials: (locationId: string) =>
    apiClient
      .get<ApiResponse<LocationCredentials | null>>('/insurance/credentials/location', {
        params: { locationId },
      })
      .then((r) => r.data.data),

  saveLocationCredentials: (input: LocationCredentialsInput) =>
    apiClient
      .put<ApiResponse<LocationCredentials>>('/insurance/credentials/location', input)
      .then((r) => r.data.data),

  listProviderCredentials: (locationId: string) =>
    apiClient
      .get<ApiResponse<ProviderCredentialRow[]>>('/insurance/credentials/providers', {
        params: { locationId },
      })
      .then((r) => r.data.data),

  saveProviderCredential: (staffMemberId: string, input: ProviderCredentialInput) =>
    apiClient
      .put<
        ApiResponse<ProviderCredentialRow>
      >(`/insurance/credentials/providers/${staffMemberId}`, input)
      .then((r) => r.data.data),

  addProvider: (input: AddProviderInput) =>
    apiClient
      .post<ApiResponse<ProviderCredentialRow>>('/insurance/credentials/providers', input)
      .then((r) => r.data.data),

  getReadiness: (locationId: string) =>
    apiClient
      .get<ApiResponse<SetupReadiness>>('/insurance/readiness', { params: { locationId } })
      .then((r) => r.data.data),
};
